import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  quantity?: number;
}

interface appState {
  sellWithScanner: boolean;
  // Cart management
  selectedProducts: Product[];
  quantities: Record<number, number>;
  totalPrice: number;
  boughtPrice: number;
}

const initialState: appState = {
  sellWithScanner: true,
  // Cart management
  selectedProducts: [],
  quantities: {},
  totalPrice: 0,
  boughtPrice: 0,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {


    // Cart actions
    addProduct: (
      state,
      action: PayloadAction<{ product: Product; quantity?: number }>
    ) => {
      const { product, quantity = 1 } = action.payload;
      const existingIndex = state.selectedProducts.findIndex(
        (p) => p.id_product === product.id_product
      );

      if (existingIndex === -1) {
        state.selectedProducts.unshift(product);
        state.quantities[product.id_product] = quantity;
      } else {
        // If product already exists, add to quantity
        const currentQuantity = state.quantities[product.id_product] || 1;
        state.quantities[product.id_product] = currentQuantity + quantity;
      }
    },

    updateQuantity: (
      state,
      action: PayloadAction<{ productId: number; quantity: number }>
    ) => {
      const { productId, quantity } = action.payload;
      state.quantities[productId] = quantity;
    },

    removeProduct: (state, action: PayloadAction<number>) => {
      const productId = action.payload;
      state.selectedProducts = state.selectedProducts.filter(
        (p) => p.id_product !== productId
      );
      delete state.quantities[productId];
    },

    clearCart: (state) => {
      state.selectedProducts = [];
      state.quantities = {};
      state.totalPrice = 0;
      state.boughtPrice = 0;
    },

    setSelectedProducts: (state, action: PayloadAction<Product[]>) => {
      state.selectedProducts = action.payload;
    },

    setQuantities: (
      state,
      action: PayloadAction<
        | Record<number, number>
        | ((prev: Record<number, number>) => Record<number, number>)
      >
    ) => {
      if (typeof action.payload === "function") {
        state.quantities = action.payload(state.quantities);
      } else {
        state.quantities = action.payload;
      }
    },

    setTotalPrice: (
      state,
      action: PayloadAction<number | ((prev: number) => number)>
    ) => {
      if (typeof action.payload === "function") {
        state.totalPrice = action.payload(state.totalPrice);
      } else {
        state.totalPrice = action.payload;
      }
    },

    setBoughtPrice: (state, action: PayloadAction<number>) => {
      state.boughtPrice = action.payload;
    },

    calculateTotals: (state) => {
      const total = state.selectedProducts.reduce((acc, item) => {
        return (
          acc +
          (state.quantities[item.id_product] || 1) * parseFloat(item.price_sell)
        );
      }, 0);

      const bought = state.selectedProducts.reduce((acc, item) => {
        return (
          acc +
          (state.quantities[item.id_product] || 1) *
          parseFloat(item.price_bought)
        );
      }, 0);

      state.totalPrice = total;
      state.boughtPrice = bought;
    },
  },
});

const productActions = productSlice.actions;
const productReducer = productSlice.reducer;
export { productActions, productReducer };
